// ---------------------------------------------------------------------------
// Navigation config for the app-shell (extracted VERBATIM from the original
// app-shell.tsx by SPEC-244 §Sprint5 IA-18 — pure data, no logic).
// Read by `sub-components/AppSidebar.tsx`.
// PR #1101 (re-aim): the source-scan test
// `bug-sprint133-track-c-cuponera-wiring.test.ts` now reads THIS file
// (instead of the deleted `app-shell.tsx`) for the sidebar href assertions.
// ---------------------------------------------------------------------------

import * as React from "react"
import {
  Home,
  User,
  Radio,
  Wallet,
  Network,
  Settings,
  LayoutDashboard,
  Shield,
  BarChart3,
  Package,
  // SPEC-242 Track D #19 (Paquetes/Combos): PackageCheck icon for the new
  // "Combos" sidebar item — links to /seller/combos (the seller's bundle
  // CRUD page). Distinct from `Package` (already used by the "Paquetes"
  // item linking to /app/packages — the user's package/plan management
  // page). The check-mark variant conveys "approved bundle" semantics
  // (bundles require admin approval per INV-MKT-20, same as individual
  // products). Standard lucide icon — no new dependency.
  // Spec ref: SPEC-242 §4 (Track D — Paquetes/Combos APROBADO por Founder
  //           2026-09-19), §4.5.1 (seller UI); SPEC-NEGOCIO §10.14
  //           (combos); ADR-039 + enmienda INV-MKT-21.
  PackageCheck,
  // SPEC-242 Track C #14 (Cuponera Cuponatic-style — Profileia Market):
  // `Ticket` icon for the new "Cupones" sidebar item — links to
  // /seller/cupones (the seller's coupon CRUD + redemption code
  // validator page). `Gift` icon for the new "Gift Cards" sidebar item —
  // links to /seller/gift-cards (the seller's gift card CRUD + redemption
  // code validator page). Standard lucide icons — no new dependency.
  // Spec ref: SPEC-242 §3 (Track C — Cuponera Cuponatic-style APROBADO
  //           por Founder 2026-09-19), §3.5 (endpoints seller), §3.6.3
  //           (seller UI); SPEC-NEGOCIO §10.13 (Cuponera Externa);
  //           ADR-039.
  Ticket,
  Gift,
  // SPEC-171 (Sprint 85): Calendar icon for the "Reservas" sidebar item.
  Calendar,
  // AUDIT-065-003 fix (2026-08-23): icon for the new "Landing Pages"
  // sidebar item — maps to the existing /app/landing route (the user's
  // landing-page editor). Pre-fix, the route existed but was NOT linked
  // from the sidebar (only from the dashboard module cards), making it
  // undiscoverable.
  // Spec ref: docs/SPEC-065-QA-AUDIT-2026-08-23-V2.md §AUDIT-065-003
  LayoutTemplate,
  // SPEC-071 (Academia Profileia · Fase 0): icon for the new "Academia"
  // sidebar item. Links to /app/academy (the catalog page).
  // Spec ref: SPEC-071 §5.3, SPEC-NEGOCIO §1.6, ADR-038.
  GraduationCap,
  // SPEC-081 Fase 1 (Sistema de Créditos — Profileia Market): icon for
  // the new "Profileia Market" sidebar item. Links to
  // /market/comprar/credits (the credit-purchase page). Gated by the
  // `market_credits_enabled` feature flag (Task A added the field to
  // the `FeatureFlags` interface). When the flag is OFF, the item is
  // hidden — the backend independently rejects requests to
  // /api/market/credits/* with 403 MARKET_CREDITS_DISABLED (defense in
  // depth). Spec ref: SPEC-081 Fase 1 §6.5 (navigation), ADR-039.
  ShoppingBag,
  // SPEC-081 Fase 2 (Portal de Vendedores — Profileia Market): icon for
  // the new "Portal Vendedor" sidebar item. Links to /seller/dashboard
  // (the seller dashboard page). Gated by the `market_sellers_enabled`
  // feature flag (Task A added the field to the `FeatureFlags`
  // interface — same pattern as Fase 1's `market_credits_enabled`).
  // When the flag is OFF, the item is hidden — the backend
  // independently rejects requests to /api/seller/* with 403
  // MARKET_SELLERS_DISABLED (defense in depth). Spec ref: SPEC-081
  // Fase 2 §6.11 (navigation), ADR-039 + enmienda INV-MKT-21.
  Store,
  // SPEC-146 (Sprint 70, 2026-09-07) — F-001 Notificaciones In-App.
  // ADR-043. Icon for the "Notificaciones" sidebar item. Standard lucide
  // icon — no new dependency (lucide-react already in package.json).
  Bell,
  // Sprint 88 — Profileia AI Hub: icon for "Asistentes IA" sidebar item.
  Bot,
  // SPEC-185 (Sprint 92-99): icon for "AI Pro Army" sidebar item.
  Brain,
  // Sprint 134 (2026-09-19): icons for Mi Red subitems.
  Users,
  UserCheck,
  MessageSquare,
  // PR #741 — Trophy icon for the Leaderboard sidebar item
  Trophy,
  // PR #1431 — ClipboardList icon for the new "Mis Compras" sidebar item
  // (buyer's order history). Standard lucide icon — conveys "list of orders"
  // semantics, distinct from ShoppingBag (catalog browsing) and Package
  // (user's plan package).
  ClipboardList,
  // SPEC-AI-CONTEXT-FILE-STORAGE PR2 (2026-10-21) — HardDrive icon for the
  // new "Almacenamiento" sidebar item linking to /app/almacenamiento (the
  // user's cloud storage page — avatars, covers, KYC, market, AI context,
  // landing, general). Gated by the `user_storage_enabled` feature flag
  // (per SPEC §7.3 — "default true" per Founder decision). Standard lucide
  // icon — conveys "cloud storage / drive" semantics. Spec ref: SPEC-AI-
  // CONTEXT-FILE-STORAGE §7.3, §11 (PR2).
  HardDrive,
  // CONECTA-PR3-FE-HUB-OPT-IN (2026-10-22) — Sparkles icon for the new
  // "Conecta" sidebar item linking to /app/conecta (the user's Conecta
  // settings page — opt-in toggle, categories, location, danger zone).
  // Always visible (no feature flag gate — the page handles the empty state
  // + the KYC gate natively). Grouped with `aprendizaje` (Herramientas) so
  // it sits next to Profileia Hub + Reservas (the other Hub-tool items).
  // Standard lucide icon — no new dependency. Spec ref: SPEC-245 §6.1
  // (Hub opt-in flow), §16 PR3 plan.
  Sparkles,
  // Bug A3 (FE subagent — "dar acceso a un historial de compras de
  // paquetes que ha hecho el usuario"): History icon for the new
  // "Historial de Paquetes" sidebar item linking to
  // /app/historial-paquetes (the user's package purchase history page —
  // date, package type, amount, role Networker/Cliente, status per row).
  // Always visible (no feature flag gate — the page is a pure read over
  // the existing GET /api/deposit/history endpoint filtered to package
  // deposits). Grouped with `dinero` so it sits next to "Billetera" +
  // "Paquetes" (the other money/monetization items). Standard lucide icon
  // — conveys "history / past purchases" semantics, distinct from
  // Package (the user's current plan management). No new dependency.
  // §6 Anti-Invención: pure navigation wiring — no new business logic.
  History,
} from "lucide-react"

interface NavItem {
  title: string
  // AUDIT-065-003 fix: added '/app/landing' to the NavItem href union
  // so the new "Landing Pages" sidebar item type-checks.
  // SPEC-071: added '/app/academy' to the union for the new Academia
  // sidebar item.
  // SPEC-081 Fase 1 §6.5: added '/market/comprar/credits' to the union
  // for the new "Profileia Market" sidebar item (gated by the
  // `market_credits_enabled` feature flag).
  // SPEC-081 Fase 2 §6.11: added '/seller/dashboard' to the union for
  // the new "Portal Vendedor" sidebar item (gated by the
  // `market_sellers_enabled` feature flag).
  // SPEC-081 Fase 3 §6.8: added '/market' to the union for the new
  // "Market" sidebar item linking to the public product catalog (gated
  // by the `market_products_enabled` feature flag — Task A added the
  // field to the `FeatureFlags` interface).
  // SPEC-146 (Sprint 70, 2026-09-07): added '/app/notifications' to the
  // union for the new "Notificaciones" sidebar item (F-001 Notificaciones
  // In-App — ADR-043). The item shows a badge with unreadCount.
  // SPEC-242 Track D #19 (2026-09-19): added '/seller/combos' to the
  // union for the new "Combos" sidebar item linking to the seller's
  // bundle CRUD page (gated by `market_sellers_enabled` — same flag as
  // "Portal Vendedor" since bundles are a seller-only feature per
  // SPEC-242 §4.5.1).
  // SPEC-242 Track C #14 (2026-09-19): added '/seller/cupones' +
  // '/seller/gift-cards' to the union for the new "Cupones" + "Gift
  // Cards" sidebar items linking to the seller's coupon + gift card CRUD
  // pages (gated by `market_sellers_enabled` — same flag as "Portal
  // Vendedor" + "Combos" since cupones + gift cards are seller-only
  // features per SPEC-242 §3.5 seller endpoints).
  // SPEC-242 Track C #15 (Cuponera — Buyer UI): added '/app/mis-cupones'
  // to the union for the new "Mis Cupones" sidebar item linking to the
  // buyer's purchased coupons + gift cards page (gated by
  // `market_products_enabled` — same flag as "Market" since the buyer
  // can only have purchases if the products flag is on; coupons inherit
  // the products gate per `market.routes.ts` line 83).
  // Sprint 138 (PR-D: Feature 1.A — Networker Welcome): added
  // '/app/welcome-message' for the new "Mensaje de Bienvenida" sidebar
  // item (gated to NO feature flag — all networkers can create their
  // welcome message per INV-WELCOME-01). Spec ref: SPEC-NEGOCIO §10.15.
  // SPEC-AI-CONTEXT-FILE-STORAGE PR2 (2026-10-21): added
  // '/app/almacenamiento' for the new "Almacenamiento" sidebar item
  // (gated by `user_storage_enabled` — default true per SPEC §7.3).
  // The route lives at `frontend/src/app/app/almacenamiento/page.tsx`
  // (Next.js App Router — the `/app/` segment is the dashboard layout
  // group; the URL is `/app/almacenamiento`). Spec ref: SPEC-AI-
  // CONTEXT-FILE-STORAGE §7.1, §7.3.
  // CONECTA-PR3-FE-HUB-OPT-IN (2026-10-22): added '/app/conecta' for the
  // new "Conecta" sidebar item linking to /app/conecta (the user's Conecta
  // settings page — opt-in flow with 6 eligibility gates per SPEC-245 §6.2).
  // Always visible — no feature flag (the page renders the empty state +
  // KYC gate natively). Spec ref: SPEC-245 §6.1 (Hub opt-in flow) + §16 PR3.
  // Bug A3 (FE subagent): added '/app/historial-paquetes' to the NavItem
  // href union for the new "Historial de Paquetes" sidebar item linking to
  // /app/historial-paquetes (the user's package purchase history page).
  // Always visible — no feature flag (the page is a pure read over the
  // existing GET /api/deposit/history endpoint filtered to package
  // deposits). §6 Anti-Invención: pure navigation wiring.
  href: '/app' | '/app/profile' | '/app/conferences' | '/app/packages' | '/app/historial-paquetes' | '/app/wallet' | '/app/network' | '/app/network/unilevel' | '/app/network/directos' | '/app/analytics' | '/app/settings' | '/app/kyc' | '/app/landing' | '/app/academy' | '/app/notifications' | '/app/reservations' | '/app/asistentes-ia' | '/app/asistentes-ia-pro' | '/app/mis-cupones' | '/app/leaderboard' | '/app/welcome-message' | '/app/almacenamiento' | '/app/conecta' | '/market/comprar/credits' | '/seller/dashboard' | '/seller/tiendas' | '/seller/productos' | '/seller/productos/importar-dropi' | '/seller/ordenes' | '/seller/combos' | '/seller/cupones' | '/seller/gift-cards' | '/seller/welcome-message' | '/seller/reservas' | '/market' | '/market/ordenes' | '/conecta'
  icon: React.ElementType
  // SPEC-081 Fase 1 §6.5: optional feature-flag gate. When set, the nav
  // item is only rendered if the corresponding flag in `FeatureFlags` is
  // `true`. Currently used by the "Profileia Market" item
  // (`market_credits_enabled`). Other items have no gate (always visible).
  // SPEC-081 Fase 2 §6.11: extended union with `market_sellers_enabled`
  // for the new "Portal Vendedor" item.
  // SPEC-081 Fase 3 §6.8: extended union with `market_products_enabled`
  // for the new "Market" (catalog) item — the public product catalog
  // (`/market`) is gated independently from credit purchase (`/market/
  // comprar/credits`) so Founder can soft-launch one without the other.
  // SPEC-AI-CONTEXT-FILE-STORAGE PR2 (2026-10-21): extended union with
  // `user_storage_enabled` for the new "Almacenamiento" sidebar item.
  // Per SPEC §7.3 "default true" — AppSidebar treats `undefined` as
  // `true` (fail-open). PR3 (BE) will wire the env var
  // `FF_USER_STORAGE_ENABLED` + add the required (non-optional) field
  // here when BE returns the field explicitly.
  featureFlag?: 'market_credits_enabled' | 'market_sellers_enabled' | 'market_products_enabled' | 'user_storage_enabled'
  // Sidebar grouping (refactor — flat list → grouped nav). Each item belongs
  // to exactly one of 6 user-visible groups rendered with a label header
  // (Inicio, Dinero, Mi Red, Marketplace, Aprendizaje, Cuenta). Labels are
  // user-facing Spanish strings (P-263: no internal refs in UI).
  group: 'inicio' | 'dinero' | 'mi-red' | 'marketplace' | 'aprendizaje' | 'cuenta'
}

// User-visible navigation groups. Rendered in this exact order in the sidebar
// (Inicio → Dinero → Mi Red → Marketplace → Aprendizaje → Cuenta). The `id`
// matches the `NavItem.group` literal union above; the `label` is the
// user-visible header shown above each group's items (hidden when the sidebar
// is collapsed via the `state !== "collapsed"` check in the render).
// Sprint 139 (Founder directive 2026-09-23): moved Herramientas group up
// to be right after Inicio (Dashboard) and before Dinero — the Founder
// wants Herramientas more discoverable, not buried at position 5.
const NAV_GROUPS = [
  { id: 'inicio', label: 'Inicio' },
  { id: 'aprendizaje', label: 'Herramientas' },
  { id: 'dinero', label: 'Dinero' },
  { id: 'mi-red', label: 'Mi Red' },
  { id: 'marketplace', label: 'Marketplace' },
  { id: 'cuenta', label: 'Cuenta' },
] as const

const NAV_ITEMS: NavItem[] = [
  { title: "Dashboard", href: "/app", icon: Home, group: 'inicio' },
  // SPEC-163 P-285 (Sprint 81 QA, 2026-09-10): Founder directive —
  // rename "Mi Perfil" → "Profileia Hub" because that's the canonical
  // name per SPEC-095 (the user's public Profile Hub at /{username} is
  // called "Profileia Hub"). The /app/profile page lets the user edit
  // the data shown on their Profile Hub (vCard, CTAs, branding, metrics).
  // Naming consistency: sidebar item matches the public-facing concept.
  //
  // P-UX-009 (SPEC-165): the QA UX Specialist reported a hydration flash
  // "Mi Perfil" → "Profileia Hub" on hard reload. Verified 2026-09-10
  // (post-PR #353 merge): NO "Mi Perfil" label remains in any SSR-rendered
  // path — the sidebar NAV_ITEMS, the mobile dropdown, the desktop
  // dropdown, and the /app/profile page document.title all use
  // "Profileia Hub" consistently. The root `<html>` + `<body>` already
  // have `suppressHydrationWarning` (PR #350 P-277). Conclusion: the
  // flash reported by QA was likely observed on the pre-PR-#353 staging
  // build. Marked NO-OP — full re-verification deferred to R2 post-deploy.
  // PR #734 — Founder directive: "Pasa el item Profileia Hub a Herramientas,
  // ya que es la tarjeta digital y es una herramienta".
  { title: "Profileia Hub", href: "/app/profile", icon: User, group: 'aprendizaje' },
  { title: "Salas", href: "/app/conferences", icon: Radio, group: 'aprendizaje' },
  // SPEC-171 (Sprint 85, 2026-09-10): Internal reservation system.
  // Sidebar item for /app/reservations (availability editor + list).
  { title: "Reservas", href: "/app/reservations", icon: Calendar, group: 'aprendizaje' },
  { title: "Billetera", href: "/app/wallet", icon: Wallet, group: 'dinero' },
  { title: "Paquetes", href: "/app/packages", icon: Package, group: 'dinero' },
  // Bug A3 (FE subagent — "dar acceso a un historial de compras de
  // paquetes que ha hecho el usuario"): "Historial de Paquetes" sidebar
  // item linking to /app/historial-paquetes (the user's package purchase
  // history page). Placed immediately after "Paquetes" so the package-
  // related items group together in the "Dinero" group (Billetera →
  // Paquetes → Historial de Paquetes → Comprar Créditos). No feature
  // flag gate — the page is a pure read over GET /api/deposit/history
  // filtered to package deposits (truthy `packageSku`). Always visible.
  // §6 Anti-Invención: pure navigation wiring — no new business logic.
  { title: "Historial de Paquetes", href: "/app/historial-paquetes", icon: History, group: 'dinero' },
  // AUDIT-065-003 fix (2026-08-23): "Landing Pages" sidebar item — links
  // to the existing /app/landing route (the user's landing-page editor).
  // Placed between "Paquetes" and "Mi Red" so it groups with the other
  // marketing/growth features (per the audit spec).
  // Spec ref: docs/SPEC-065-QA-AUDIT-2026-08-23-V2.md §AUDIT-065-003
  { title: "Landing Pages", href: "/app/landing", icon: LayoutTemplate, group: 'aprendizaje' },
  // SPEC-071 (Academia Profileia · Fase 0): "Academia" sidebar item —
  // links to /app/academy (the catalog page). Placed after Landing Pages
  // so it groups with the other growth/learning features. Per SPEC-071
  // §5.3, the icon is GraduationCap from lucide-react (already a
  // dependency — no new dep added).
  // Spec ref: SPEC-071 §5.3, SPEC-NEGOCIO §1.6, ADR-038.
  { title: "Academia", href: "/app/academy", icon: GraduationCap, group: 'aprendizaje' },
  // SPEC-081 Fase 1 (Sistema de Créditos — Profileia Market): "Comprar
  // Créditos" sidebar item — links to /market/comprar/credits (the credit-
  // purchase page). Gated by `market_credits_enabled` (Task A added the
  // field to FeatureFlags; this AppSidebar fetches flags + filters the
  // item). Placed after Academia so it groups with the other growth /
  // monetization features. Per SPEC-081 Fase 1 §6.5, the icon is
  // ShoppingBag from lucide-react (already a dependency — no new dep
  // added). Spec ref: SPEC-081 Fase 1 §6.5, ADR-039.
  //
  // P-UX-004 (SPEC-165): renamed from "Profileia Market" → "Comprar
  // Créditos" — el QA UX Specialist reportó fricción: un usuario que
  // quería ir al marketplace hacía click en "Profileia Market" y terminaba
  // en compra de créditos. "Market" (sin prefijo) es el único entrypoint
  // al marketplace público; "Comprar Créditos" deja explícita la acción.
  // Cambio puramente cosmético — NO cambia URLs ni lógica.
  { title: "Comprar Créditos", href: "/market/comprar/credits", icon: ShoppingBag, featureFlag: 'market_credits_enabled', group: 'dinero' },
  // SPEC-081 Fase 2 (Portal de Vendedores — Profileia Market): "Portal
  // Vendedor" sidebar item — links to /seller/dashboard (the seller
  // dashboard page). Gated by `market_sellers_enabled` (Task A added
  // the field to FeatureFlags; this AppSidebar fetches flags + filters
  // the item — same pattern as `market_credits_enabled`). Placed
  // immediately after "Profileia Market" so it groups with the other
  // monetization features. Per SPEC-081 Fase 2 §6.11, the icon is Store
  // from lucide-react (already a dependency — no new dep added).
  // Spec ref: SPEC-081 Fase 2 §6.11, ADR-039 + enmienda INV-MKT-21.
  // PR #734 — Founder directive: "en Marketplace deja el item Market de primero".
  { title: "Market", href: "/market", icon: ShoppingBag, featureFlag: 'market_products_enabled', group: 'marketplace' },
  // PR #1431 — "Mis Compras" sidebar item linking to /market/ordenes (the
  // buyer's order history page with filter by status: pendiente, enviada,
  // entregada, completada, cancelada, etc.). Gated by
  // `market_products_enabled` (same flag as "Market" — the buyer can only
  // have purchases if the products flag is on; the order history inherits
  // the products gate per `order.routes.ts` line 65 — the router-level
  // `requireMarketOrdersEnabled` is applied to ALL `/api/market/orders/*`
  // routes). Placed immediately after "Market" so the buyer-side Market
  // items group together (Market → Mis Compras → Mis Cupones).
  //
  // Founder report 2026-10-18: "No hay UI de subitem que redireccione a
  // ver Mis compras, para ver las completadas, pendientes, en proceso etc"
  // — before this fix, the only way to reach /market/ordenes was by
  // manually typing the URL or via a back-link from an order detail page
  // (which itself was only reachable via notification or direct URL).
  // The icon is `ClipboardList` (standard lucide icon — conveys "order
  // list / order history" semantics, distinct from `ShoppingBag` used by
  // "Market" for catalog browsing).
  // Spec ref: SPEC-081 Fase 4 §5.1 (Buyer order routes — GET /market/orders),
  //           ADR-043 §3 (canonical notification URLs).
  { title: "Mis Compras", href: "/market/ordenes", icon: ClipboardList, featureFlag: 'market_products_enabled', group: 'marketplace' },
  { title: "Portal Vendedor", href: "/seller/dashboard", icon: Store, featureFlag: 'market_sellers_enabled', group: 'marketplace' },
  // Founder directive 2026-10-31 (seller UX — Productos + Mis Ventas):
  // The seller had NO sidebar items for "Productos" or "Mis Ventas" —
  // they could only reach /seller/dashboard (KPIs) + /seller/tiendas
  // (already had "Mis Tiendas" below). To publish products they had to
  // know /seller/productos. To see their sales + chat with buyers they
  // had to know /seller/ordenes. The Founder reported: "no tengo UI de
  // sus ventas y los chats con los compradores". Fix: add 2 sidebar
  // items grouping with "Portal Vendedor":
  //   1. Productos → /seller/productos (the seller's product CRUD table).
  //   2. Mis Ventas → /seller/ordenes (the seller's received orders list
  //      with chat dialog per order — the Founder's PRIMARY ask: "que
  //      aparezca un item de Mis ventas y allí pueda administrar todo").
  // Both gated by `market_sellers_enabled`. Icons: Package (Productos —
  // reuse), ShoppingBag (Mis Ventas — reuse, conveys "sales/received
  // orders"). Anti-P-382: root fix — add the nav affordances so the
  // seller has a clear path to manage products + sales.
  { title: "Productos", href: "/seller/productos", icon: Package, featureFlag: 'market_sellers_enabled', group: 'marketplace' },
  { title: "Mis Ventas", href: "/seller/ordenes", icon: ShoppingBag, featureFlag: 'market_sellers_enabled', group: 'marketplace' },
  // Founder directive 2026-10-30 (SUBSTORES follow-up): "Mis Tiendas"
  // sidebar item — links to /seller/tiendas (the sub-stores management
  // page: list, create, set-primary, soft-delete). Gated by
  // `market_sellers_enabled` (same flag as "Portal Vendedor"). Placed
  // immediately after "Portal Vendedor" so the seller-specific items
  // group together. The icon is Store (same as Portal Vendedor — but
  // with a different label so the user distinguishes "dashboard" from
  // "store management"). Spec ref: SUBSTORES PR #1511.
  { title: "Mis Tiendas", href: "/seller/tiendas", icon: Store, featureFlag: 'market_sellers_enabled', group: 'marketplace' },
  // SPEC-242 Track D #19 (Paquetes/Combos — Profileia Market): "Combos"
  // sidebar item — links to /seller/combos (the seller's bundle CRUD
  // page). Gated by `market_sellers_enabled` (same flag as "Portal
  // Vendedor" — bundles are a seller-only feature per SPEC-242 §4.5.1;
  // a seller must be active to create/edit bundles). Placed immediately
  // after "Portal Vendedor" so the seller-specific items group together
  // (Portal Vendedor → Combos → Cupones → Gift Cards → Market). The
  // icon is PackageCheck (distinct from `Package` already used by
  // "Paquetes" — the check variant conveys "approved bundle" semantics
  // since bundles require admin approval per INV-MKT-20).
  // Spec ref: SPEC-242 §4 (Track D — Paquetes/Combos APROBADO por Founder
  //           2026-09-19), §4.5.1 (seller UI); SPEC-NEGOCIO §10.14
  //           (combos); ADR-039 + enmienda INV-MKT-21.
  // V124.2 BUG #6 anti-regression: this href is INTENTIONALLY
  // `/seller/combos` (the seller's own CRUD page with create / edit /
  // delete — full ProductBundleForm wizard at
  // `components/seller/product-bundle-form.tsx`). Do NOT change this to
  // `/admin/market/combos` — that route is the SUPERADMIN moderation
  // panel (admin-only, rejects non-admin sessions with 403). Changing
  // the seller sidebar to point at the admin URL would lock sellers
  // out of combo creation (the exact regression reported in BUG #6).
  { title: "Combos", href: "/seller/combos", icon: PackageCheck, featureFlag: 'market_sellers_enabled', group: 'marketplace' },
  // SPEC-242 Track C #14 (Cuponera Cuponatic-style — Profileia Market):
  // "Cupones" sidebar item — links to /seller/cupones (the seller's
  // coupon CRUD + redemption code validator page). Gated by
  // `market_sellers_enabled` (same flag as "Portal Vendedor" + "Combos"
  // — cupones are a seller-only feature per SPEC-242 §3.5 seller
  // endpoints; a seller must be active to create/edit/validate coupons).
  // Placed immediately after "Combos" so the seller-specific items
  // group together (Portal Vendedor → Combos → Cupones → Gift Cards →
  // Market). The icon is Ticket (standard lucide icon — conveys
  // "coupon / voucher" semantics, distinct from PackageCheck used by
  // "Combos").
  // Spec ref: SPEC-242 §3 (Track C — Cuponera Cuponatic-style APROBADO
  //           por Founder 2026-09-19), §3.3.1 (Coupon model), §3.5
  //           (endpoints seller), §3.6.3 (seller UI); SPEC-NEGOCIO
  //           §10.13 (Cuponera Externa); ADR-039.
  { title: "Cupones", href: "/seller/cupones", icon: Ticket, featureFlag: 'market_sellers_enabled', group: 'marketplace' },
  // SPEC-242 Track C #14 (Cuponera Cuponatic-style — Profileia Market):
  // "Gift Cards" sidebar item — links to /seller/gift-cards (the
  // seller's gift card CRUD + redemption code validator page). Gated by
  // `market_sellers_enabled` (same flag as "Portal Vendedor" + "Combos"
  // + "Cupones"). Placed immediately after "Cupones" so the seller-
  // specific cuponera items group together. The icon is Gift (standard
  // lucide icon — conveys "gift card / regalo" semantics per SPEC-242
  // §3.4.2 "Siempre es regalo: SÍ").
  // Spec ref: SPEC-242 §3 (Track C — Cuponera Cuponatic-style APROBADO
  //           por Founder 2026-09-19), §3.3.3 (GiftCard model), §3.5
  //           (endpoints seller), §3.6.3 (seller UI); SPEC-NEGOCIO
  //           §10.13 (Cuponera Externa); ADR-039.
  { title: "Gift Cards", href: "/seller/gift-cards", icon: Gift, featureFlag: 'market_sellers_enabled', group: 'marketplace' },
  // Sprint 138 (PR-E: Feature 1.B — Seller Welcome): new sidebar item
  // for the seller welcome message editor. Gated by
  // `market_sellers_enabled` (same flag as "Portal Vendedor" +
  // "Combos" + "Cupones" + "Gift Cards" — only sellers can create their
  // welcome message for buyers per ADR-058 §Feature 1.B).
  // Placed after "Gift Cards" so all seller CRUD items group together.
  // Uses MessageSquare icon (already imported for the networker welcome
  // item at group 'cuenta' — no visual confusion because they're in
  // different groups).
  { title: "Mensaje a Compradores", href: "/seller/welcome-message", icon: MessageSquare, featureFlag: 'market_sellers_enabled', group: 'marketplace' },
  // FE-Fase-3 Calendar Seller Reservas (2026-10-29): "Reservas" sidebar
  // item linking to /seller/reservas (the seller's booking calendar
  // dashboard — month grid view of all bookings across the seller's
  // service products + a DateBlock editor to block out dates for external
  // bookings, maintenance, etc.). Gated by `market_sellers_enabled` (same
  // flag as "Portal Vendedor" — the booking dashboard is a seller-only
  // view; only the seller of a service product with calendarEnabled sees
  // bookings). Placed after "Mensaje a Compradores" so all seller CRUD
  // items group together (Portal Vendedor → Combos → Cupones → Gift Cards
  // → Mensaje a Compradores → Reservas). The icon reuses `Calendar`
  // (already imported for the /app/reservations "Reservas" item in
  // 'aprendizaje') — same concept (calendar), different group
  // ('marketplace' vs 'aprendizaje'), no visual confusion because they're
  // visually separated by the group headers. Anti-P-382: reuses the
  // existing `api.booking.*` namespace (getSellerBookings + getDateBlocks
  // + blockDates + unblockDates); no parallel implementation on the FE.
  // Spec ref: SPEC-MARKETPLACE-BOOKING-CALENDAR §5 (booking routes —
  //           GET /booking/seller/bookings, POST /seller/block, DELETE
  //           /seller/block/:blockId, GET /seller/blocks/:productId).
  { title: "Reservas", href: "/seller/reservas", icon: Calendar, featureFlag: 'market_sellers_enabled', group: 'marketplace' },
  // SPEC-081 Fase 3 (Publicación de Productos — Profileia Market):
  // "Market" sidebar item — links to /market (the public product
  // catalog). Gated by `market_products_enabled` (Task A added the
  // field to FeatureFlags). Placed after "Portal Vendedor" so the
  // three Profileia Market nav items group together:
  //   1. /market/comprar/credits (comprar créditos)
  //   2. /market (catálogo público de productos — Fase 3) — PR #734: now first
  //   3. /seller/dashboard (portal vendedor)
  // The icon reuses ShoppingBag (already imported for Fase 1's
  // "Profileia Market" item) — no new lucide-react import needed.
  // Spec ref: SPEC-081 Fase 3 §6.8, ADR-039, SPEC-NEGOCIO §10.4
  //           (INV-MKT-20) + §10.11 (15 categorías).
  // PR #734 — Market moved to first position in marketplace group (above
  // Portal Vendedor). The old duplicate item here was removed.
  // SPEC-242 Track C #15 (Cuponera — Buyer UI — Mis Cupones): "Mis
  // Cupones" sidebar item — links to /app/mis-cupones (the buyer's
  // purchased coupons + gift cards page with tabs for each). Gated by
  // `market_products_enabled` (same flag as "Market" — the buyer can
  // only have purchases if the products flag is on; coupons + gift
  // cards inherit the products gate per `market.routes.ts` line 83 —
  // the router-level `requireMarketProductsEnabled` is applied to ALL
  // `/api/market/*` routes including `/coupons/my-purchases` + `/gift-
  // cards/my-purchases`). Placed immediately after "Market" so the
  // buyer-side Market items group together (Market → Mis Cupones → Mi
  // Red). The icon is `Ticket` (already imported for the seller-side
  // "Cupones" item — reused here for visual consistency, since the
  // buyer's "Mis Cupones" page shows the same coupon concept from the
  // buyer's perspective).
  // Spec ref: SPEC-242 §3 (Track C — Cuponera Cuponatic-style), §3.5
  //           (GET /api/market/coupons/my-purchases + /gift-cards/my-
  //           purchases), §3.6.4 (detalle gift card UI — similar shape
  //           for coupons); SPEC-NEGOCIO §10.13 (Cuponera Externa);
  //           ADR-039 + enmienda INV-MKT-21.
  { title: "Mis Cupones", href: "/app/mis-cupones", icon: Ticket, featureFlag: 'market_products_enabled', group: 'marketplace' },
  { title: "Matriz Viva", href: "/app/network", icon: Network, group: 'mi-red' },
  { title: "Unilevel de Clientes", href: "/app/network/unilevel", icon: Users, group: 'mi-red' },
  { title: "Mis Directos", href: "/app/network/directos", icon: UserCheck, group: 'mi-red' },
  { title: "Estadísticas", href: "/app/analytics", icon: BarChart3, group: 'mi-red' },
  // Sprint 88 — Profileia AI Hub: "Asistentes IA" sidebar item linking to
  // /app/asistentes-ia (the dashboard with all 9 AI assistants). Always
  // visible (no feature flag gate — the page shows "Próximamente" cards
  // for disabled assistants, and the widget gracefully degrades to 503
  // if the hub is off). Placed after "Estadísticas" so it groups with
  // the engagement items. Spec ref: SPEC-174.1 §6.4.
  { title: "Asistentes IA", href: "/app/asistentes-ia", icon: Bot, group: 'aprendizaje' },
  // SPEC-185 (Sprint 92-99): "AI Pro Army" sidebar item linking to
  // /app/asistentes-ia-pro (the dashboard with 26 PRO agents). Always
  // visible — shows agents with isAvailable flag. Placed after "Asistentes IA"
  // so both AI modules group together. Spec ref: SPEC-185 §10.1.
  { title: "AI Pro Army", href: "/app/asistentes-ia-pro", icon: Brain, group: 'aprendizaje' },
  // SPEC-146 (Sprint 70, 2026-09-07) — F-001 Notificaciones In-App.
  // ADR-043 (decisión). Sidebar item linking to /app/notifications
  // (the notification center page). The badge with unreadCount is rendered
  // via the NotificationBadge component (mounted separately in the sidebar
  // — see below). Placed after "Estadísticas" and before "Verificar
  // Identidad" so it groups with the activity/engagement items (analytics
  // + notifications + KYC). The item is always visible (no feature flag
  // gate — the feature flag `notifications_enabled` only gates the
  // `sendNotification()` backend no-op; the UI is shown even when the
  // flag is false, in which case the page shows "Notificaciones no
  // disponibles").
  { title: "Notificaciones", href: "/app/notifications", icon: Bell, group: 'cuenta' },
  // PR #741 — Leaderboard item in Cuenta group per Founder directive
  { title: "Leaderboard", href: "/app/leaderboard", icon: Trophy, group: 'cuenta' },
  { title: "Verificar Identidad", href: "/app/kyc", icon: Shield, group: 'cuenta' },
  // Sprint 138 (PR-D: Feature 1.A — Networker Welcome): new sidebar item
  // for the welcome message editor. Grouped with Cuenta because it's
  // a personal account-setting feature (the user manages their own
  // welcome message). No feature flag — all authenticated users with
  // a referral code (networkers) can create one per INV-WELCOME-01.
  { title: "Mensaje de Bienvenida", href: "/app/welcome-message", icon: MessageSquare, group: 'cuenta' },
  // SPEC-AI-CONTEXT-FILE-STORAGE PR2 (2026-10-21) — "Almacenamiento" sidebar
  // item linking to /app/almacenamiento (the user's cloud storage page).
  // Gated by `user_storage_enabled` (per SPEC §7.3 — "default true"; the
  // FE treats `undefined` as `true` / fail-open until PR3 wires the env
  // var). Grouped with Cuenta because the storage is a personal account
  // feature (the user manages their own uploaded files — avatars, covers,
  // KYC, market, AI context, landing, general). The icon is HardDrive
  // (standard lucide icon — conveys "cloud storage / drive" semantics).
  // Spec ref: SPEC-AI-CONTEXT-FILE-STORAGE §7.3, §11 (PR2), §13 (acceptance).
  { title: "Almacenamiento", href: "/app/almacenamiento", icon: HardDrive, featureFlag: 'user_storage_enabled', group: 'cuenta' },
  // CONECTA-PR3-FE-HUB-OPT-IN (2026-10-22) — "Conecta" sidebar item linking
  // to /app/conecta (the user's Conecta settings page — opt-in flow with
  // 6 eligibility gates per SPEC-245 §6.2). Always visible (no feature
  // flag gate — the page handles the empty state + KYC gate natively).
  // Grouped with `aprendizaje` (Herramientas) so it sits next to Profileia
  // Hub + Reservas (the other Hub-tool items). The icon is Sparkles
  // (standard lucide icon — conveys "directory / sparkle" semantics).
  // Spec ref: SPEC-245 §6.1 (Hub opt-in flow), §16 PR3 plan.
  { title: "Conecta", href: "/app/conecta", icon: Sparkles, group: 'aprendizaje' },
  // Directorio Conecta público — link al directorio de profesionales
  // verificados. Los usuarios pueden descubrir otros profesionales desde
  // el sidebar interno sin ir a la landing pública.
  { title: "Directorio Conecta", href: "/conecta", icon: Users, group: 'aprendizaje' },
  { title: "Configuración", href: "/app/settings", icon: Settings, group: 'cuenta' },
]

export type { NavItem }
export { NAV_GROUPS, NAV_ITEMS }
